/**
 * Public Booking Page — /reserver-prive
 * Permet aux clients de réserver un cours privé avec Ralph, Sonia ou Karl.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Calendar, Clock, User, Check, ArrowRight, Loader2 } from "lucide-react";
import { MemberPackages } from "../components/booking/MemberPackages";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${BACKEND}/api` });

const CAD = (v) => new Intl.NumberFormat("fr-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 }).format(v || 0);

const DAY_LABELS = ["lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi", "dimanche"];
const MONTH_LABELS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];


const DOJO_TZ = "America/Toronto"; // affichage toujours en heure du dojo (Montréal)

function formatDate(iso) {
  const d = new Date(iso);
  const p = new Intl.DateTimeFormat("en-CA", {
    timeZone: DOJO_TZ, weekday: "short", day: "numeric", month: "numeric",
  }).formatToParts(d);
  const day = p.find((x) => x.type === "day").value;
  const month = parseInt(p.find((x) => x.type === "month").value, 10);
  // weekday index via a stable formatter
  const wd = new Date(d.toLocaleString("en-US", { timeZone: DOJO_TZ })).getDay(); // 0=dim
  return `${DAY_LABELS[(wd + 6) % 7]} ${day} ${MONTH_LABELS[month - 1]}`;
}
function formatTime(iso) {
  const p = new Intl.DateTimeFormat("en-CA", {
    timeZone: DOJO_TZ, hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(new Date(iso));
  const h = p.find((x) => x.type === "hour").value.padStart(2, "0");
  const m = p.find((x) => x.type === "minute").value.padStart(2, "0");
  return `${h === "24" ? "00" : h}h${m}`;
}


// ---- i18n FR/EN (FR par défaut, conforme Québec) ----
export const I18N = {
  fr: {
    langBtn: "EN",
    heroEyebrow: "KAIZEN · Institut Impact Élite Martial",
    heroTitle: "Réserve ton cours privé",
    heroSub: "1-on-1 avec Kru Ralph ou son équipe · Choisis l'instructeur, ton créneau, et bloque ton spot en 30 secondes.",
    step1: "Étape 1 · Choisis ton instructeur",
    step2: "Étape 2 · Choisis ton créneau",
    step3: "Étape 3 · Tes coordonnées",
    giftEyebrow: "Cours privés & semi-privés",
    giftTitle: "Réserver ou offrir un cours privé",
    giftSub: "Un accompagnement personnalisé avec Kru Ralph. Choisissez une séance ou un forfait, pour vous ou en cadeau. Paiement sécurisé, code remis instantanément.",
    min30: "Séance de 30 minutes : disponible sur demande.",
    customTitle: "Montant libre (cadeau)", customSub: (a, b) => `de ${a} à ${b}`,
    buyerName: "Votre nom *", buyerEmail: "Votre courriel *", recipient: "Nom du destinataire (optionnel)", message: "Petit message (optionnel)",
    buyBtn: "Offrir cette carte-cadeau", secure: "Paiement sécurisé par Stripe · CAD",
    cats: {
      prive: { title: "Cours privés — Kick-boxing & conditionnement physique", subtitle: "Séances individuelles et forfaits personnalisés." },
      semi: { title: "Semi-privé — Jiu-jitsu Can-Ryu & jiu-jitsu brésilien", subtitle: "Une expérience plus encadrée à partager à deux." },
      abo: { title: "Abonnements cadeaux — toutes disciplines", subtitle: "Offrez des mois d'entraînement, dans la discipline de leur choix." },
    },
  },
  en: {
    langBtn: "FR",
    heroEyebrow: "KAIZEN · Impact Elite Martial Institute",
    heroTitle: "Book your private lesson",
    heroSub: "1-on-1 with Kru Ralph or his team · Choose your instructor, your time slot, and lock your spot in 30 seconds.",
    step1: "Step 1 · Choose your instructor",
    step2: "Step 2 · Choose your time slot",
    step3: "Step 3 · Your details",
    giftEyebrow: "Private & semi-private lessons",
    giftTitle: "Book or gift a private lesson",
    giftSub: "Personalized coaching with Kru Ralph. Choose a session or a package, for yourself or as a gift. Secure payment, code delivered instantly.",
    min30: "30-minute session: available upon request.",
    customTitle: "Custom amount (gift)", customSub: (a, b) => `from ${a} to ${b}`,
    buyerName: "Your name *", buyerEmail: "Your email *", recipient: "Recipient name (optional)", message: "Short message (optional)",
    buyBtn: "Gift this card", secure: "Secure payment via Stripe · CAD",
    cats: {
      prive: { title: "Private Lessons — Kickboxing & physical conditioning", subtitle: "Individual sessions and structured packages." },
      semi: { title: "Semi-Private — Can-Ryu Jiu-Jitsu & Brazilian Jiu-Jitsu", subtitle: "A more guided experience to share with a partner." },
      abo: { title: "Gift memberships — all disciplines", subtitle: "Give months of training, in the discipline of their choice." },
    },
  },
};
// Libellés EN des forfaits (par id)
export const PKG_EN = {
  prive_1h: { label: "Private lesson · 1 hour", type: "Single session" },
  prive_1h30: { label: "Private lesson · 1 hour 30", type: "Single session" },
  prive_6x1h: { label: "Package · 6 one-hour lessons", type: "Package" },
  prive_6x1h30: { label: "Package · 6 one-hour-and-30-min lessons", type: "Package" },
  prive_12x1h: { label: "Package · 12 one-hour lessons", type: "Package" },
  prive_12x1h30: { label: "Package · 12 one-hour-and-30-min lessons", type: "Package" },
  semi_1h: { label: "Semi-private · 1 hour", type: "Semi-private session" },
  semi_1h30: { label: "Semi-private · 1 hour 30", type: "Semi-private session" },
  semi_6x1h: { label: "Package · 6 one-hour semi-private lessons", type: "Package" },
  semi_6x1h30: { label: "Package · 6 one-hour-and-30-min semi-private lessons", type: "Package" },
  abo_decouverte: { label: "Discovery pack · 1 month + registration", type: "Intro" },
  abo_1mois: { label: "1 month of classes · all disciplines", type: "Gift membership" },
  abo_3mois: { label: "3 months of classes · all disciplines", type: "Gift membership" },
  abo_6mois: { label: "6 months of classes · all disciplines", type: "Gift membership" },
};


function GiftCardSection({ lang = "fr" }) {
  const t = I18N[lang];
  const [packages, setPackages] = useState([]);
  const [categories, setCategories] = useState({});
  const [limits, setLimits] = useState({ custom_min: 50, custom_max: 2000 });
  const [choice, setChoice] = useState("prive_6x1h");
  const [customAmount, setCustomAmount] = useState("");
  const [buyer, setBuyer] = useState({ buyer_name: "", buyer_email: "", recipient_name: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [paid, setPaid] = useState(null); // {gift_code, label}
  const [cardImage, setCardImage] = useState("");

  useEffect(() => {
    api.get("/gift-card/packages")
      .then((r) => { setPackages(r.data.packages || []); setCategories(r.data.categories || {}); setLimits({ custom_min: r.data.custom_min, custom_max: r.data.custom_max }); setCardImage(r.data.card_image || ""); })
      .catch(() => {});
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("gift_session");
    if (sid) {
      api.get(`/gift-card/status/${sid}`)
        .then((r) => {
          if (r.data.payment_status === "paid") {
            setPaid({ gift_code: r.data.gift_code, label: r.data.label });
            toast.success("Paiement reçu — carte-cadeau émise !");
          }
        })
        .catch(() => {});
      window.history.replaceState({}, "", "/reserver-prive");
    }
    if (params.get("gift_cancelled")) {
      toast.info("Paiement de la carte-cadeau annulé.");
      window.history.replaceState({}, "", "/reserver-prive");
    }
  }, []);

  const buy = async () => {
    if (!buyer.buyer_name.trim() || !buyer.buyer_email.trim()) {
      toast.error("Entre ton nom et ton courriel"); return;
    }
    setBusy(true);
    try {
      const body = {
        package_id: choice,
        custom_amount: choice === "custom" ? parseFloat(customAmount || "0") : 0,
        ...buyer,
        origin_url: window.location.origin,
      };
      const r = await api.post("/gift-card/checkout", body);
      window.location.href = r.data.url; // redirection Stripe sécurisée
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible de créer le paiement");
      setBusy(false);
    }
  };

  if (paid) {
    return (
      <div className="mt-14 max-w-2xl mx-auto bg-gradient-to-b from-amber-500/15 to-zinc-900 border border-amber-500/40 rounded-2xl p-8 text-center" data-testid="gift-success">
        <h3 className="text-2xl font-bold text-amber-300">🎁 Carte-cadeau émise !</h3>
        <p className="text-amber-100 mt-2">{paid.label}</p>
        <div className="mt-4 inline-block bg-zinc-950 border border-amber-500/50 rounded-xl px-6 py-4">
          <div className="text-xs uppercase tracking-widest text-amber-400/70">Code de la carte-cadeau</div>
          <div className="text-3xl font-mono font-bold text-amber-300 mt-1" data-testid="gift-code">{paid.gift_code}</div>
        </div>
        <p className="text-zinc-400 text-sm mt-4">Un reçu a été envoyé par Stripe. Présente ce code pour activer les cours privés. Au plaisir !</p>
      </div>
    );
  }

  return (
    <div className="mt-14 max-w-3xl mx-auto bg-zinc-900/70 border border-amber-500/30 rounded-2xl p-7" data-testid="gift-card-section">
      <div className="text-center mb-6">
        {cardImage && (
          <img src={cardImage} alt="Carte-cadeau premium Institut Impact Élite Martial" data-testid="gift-card-visual"
            className="w-full max-w-md mx-auto rounded-xl mb-5 shadow-[0_20px_50px_-15px_rgba(0,0,0,0.7)] border border-amber-500/20" />
        )}
        <span className="text-xs uppercase tracking-[0.3em] text-amber-400/80">{t.giftEyebrow}</span>
        <h3 className="text-2xl font-bold text-amber-200 mt-1">{t.giftTitle}</h3>
        <p className="text-zinc-400 text-sm mt-2 max-w-lg mx-auto">{t.giftSub}</p>
      </div>

      {["prive", "semi", "abo"].map((cat) => {
        const catPkgs = packages.filter((p) => p.category === cat);
        if (catPkgs.length === 0) return null;
        const meta = t.cats[cat] || categories[cat] || {};
        return (
          <div key={cat} className="mb-6" data-testid={`gift-category-${cat}`}>
            <div className="mb-2">
              <h4 className="text-amber-100 font-semibold">{meta.title || cat}</h4>
              <p className="text-zinc-500 text-xs">{meta.subtitle}</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {catPkgs.map((p) => {
                const en = PKG_EN[p.id] || {};
                return (
                <button key={p.id} type="button" onClick={() => setChoice(p.id)}
                  data-testid={`gift-pkg-${p.id}`}
                  className={`text-left rounded-xl border px-4 py-3 transition-colors ${choice === p.id ? "border-amber-400 bg-amber-500/15" : "border-zinc-700 hover:border-amber-500/50"}`}>
                  <div className="text-[10px] uppercase tracking-wider text-amber-400/70">{lang === "en" ? (en.type || p.type) : p.type}</div>
                  <div className="font-semibold text-amber-100">{lang === "en" ? (en.label || p.label) : p.label}</div>
                  <div className="text-amber-400 font-bold">{CAD(p.amount)}</div>
                </button>
                );
              })}
            </div>
            <p className="text-zinc-500 text-xs mt-2">{t.min30}</p>
          </div>
        );
      })}

      <div className="grid sm:grid-cols-2 gap-3 mb-5">
        <button type="button" onClick={() => setChoice("custom")}
          data-testid="gift-pkg-custom"
          className={`text-left rounded-xl border px-4 py-3 transition-colors ${choice === "custom" ? "border-amber-400 bg-amber-500/15" : "border-zinc-700 hover:border-amber-500/50"}`}>
          <div className="font-semibold text-amber-100">{t.customTitle}</div>
          <div className="text-zinc-400 text-xs">{t.customSub(CAD(limits.custom_min), CAD(limits.custom_max))}</div>
        </button>
      </div>

      {choice === "custom" && (
        <input type="number" min={limits.custom_min} max={limits.custom_max} value={customAmount}
          onChange={(e) => setCustomAmount(e.target.value)} placeholder={`Montant en $ (min ${limits.custom_min})`}
          data-testid="gift-custom-amount"
          className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-amber-100 mb-3" />
      )}

      <div className="grid sm:grid-cols-2 gap-3">
        <input value={buyer.buyer_name} onChange={(e) => setBuyer({ ...buyer, buyer_name: e.target.value })}
          placeholder={t.buyerName} data-testid="gift-buyer-name"
          className="bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-amber-100" />
        <input value={buyer.buyer_email} onChange={(e) => setBuyer({ ...buyer, buyer_email: e.target.value })}
          placeholder={t.buyerEmail} type="email" data-testid="gift-buyer-email"
          className="bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-amber-100" />
        <input value={buyer.recipient_name} onChange={(e) => setBuyer({ ...buyer, recipient_name: e.target.value })}
          placeholder={t.recipient} data-testid="gift-recipient"
          className="bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-amber-100 sm:col-span-2" />
        <input value={buyer.message} onChange={(e) => setBuyer({ ...buyer, message: e.target.value })}
          placeholder={t.message} data-testid="gift-message"
          className="bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-amber-100 sm:col-span-2" />
      </div>

      <button onClick={buy} disabled={busy} data-testid="gift-buy-btn"
        className="mt-5 w-full bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-zinc-950 font-bold py-3 rounded-lg flex items-center justify-center gap-2">
        {busy && <Loader2 className="w-4 h-4 animate-spin" />}
        {t.buyBtn}
        <ArrowRight className="w-4 h-4" />
      </button>
      <p className="text-center text-zinc-500 text-xs mt-3">{t.secure}</p>
    </div>
  );
}


export default function PublicBookingPage() {
  const [lang, setLang] = useState(() => {
    try {
      const saved = localStorage.getItem("iiem_booking_lang");
      if (saved === "fr" || saved === "en") return saved;
    } catch (e) {}
    const nav = ((typeof navigator !== "undefined" && (navigator.language || navigator.userLanguage)) || "").toLowerCase();
    return nav.startsWith("en") ? "en" : "fr";
  });
  useEffect(() => {
    try { localStorage.setItem("iiem_booking_lang", lang); } catch (e) {}
  }, [lang]);
  const t = I18N[lang];
  const [instructors, setInstructors] = useState([]);
  const [selectedInstructor, setSelectedInstructor] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [form, setForm] = useState({ client_name: "", client_email: "", client_phone: "", discipline: "", notes: "" });
  const [credit, setCredit] = useState(null);
  const checkCredit = async () => {
    if (!form.client_email.includes("@")) { setCredit(null); return; }
    try { const r = await api.get("/private-packages/credits", { params: { email: form.client_email, nom: form.client_name || undefined } }); setCredit(r.data.forfait); } catch { setCredit(null); }
  };
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [duration, setDuration] = useState(60);
  const [comingSoon, setComingSoon] = useState([]);
  const [interestFor, setInterestFor] = useState(null); // instructor obj
  const [interestForm, setInterestForm] = useState({ name: "", email: "", phone: "" });
  const [interestSent, setInterestSent] = useState({}); // instructor_id -> true

  useEffect(() => {
    api.get("/booking/instructors")
      .then(r => { setInstructors(r.data.instructors); setLoading(false); })
      .catch(() => { toast.error("Impossible de charger les instructeurs"); setLoading(false); });
    api.get("/booking/coming-soon")
      .then(r => setComingSoon(r.data.instructors || []))
      .catch(() => {});
  }, []);

  const submitInterest = async (instr) => {
    if (!interestForm.name.trim() || !interestForm.email.trim()) {
      toast.error("Entre ton nom et ton courriel"); return;
    }
    try {
      const r = await api.post("/booking/notify-interest", {
        instructor_id: instr.id, name: interestForm.name, email: interestForm.email, phone: interestForm.phone,
      });
      toast.success(r.data.message || "On te préviendra !");
      setInterestSent((s) => ({ ...s, [instr.id]: true }));
      setInterestFor(null);
      setInterestForm({ name: "", email: "", phone: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const loadAvailability = async (instr, dur) => {
    setAvailability(null);
    try {
      const r = await api.get(`/booking/availability?instructor_id=${instr.id}&days=14&duration=${dur}`);
      setAvailability(r.data);
    } catch (e) {
      toast.error("Erreur chargement créneaux");
    }
  };

  const pickInstructor = async (instr) => {
    setSelectedInstructor(instr);
    setSelectedSlot(null);
    await loadAvailability(instr, duration);
  };

  const changeDuration = async (dur) => {
    setDuration(dur);
    setSelectedSlot(null);
    if (selectedInstructor) await loadAvailability(selectedInstructor, dur);
  };

  const reserve = async () => {
    if (!selectedSlot || !form.client_name || !form.client_email) {
      toast.error("Nom, email et créneau requis");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/booking/reserve", {
        instructor_id: selectedInstructor.id,
        start_at: selectedSlot.start_at,
        duration_minutes: selectedSlot.duration_minutes,
        ...form,
      });
      setSuccess({ ...r.data, instructor: selectedInstructor, slot: selectedSlot });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de réservation");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-zinc-950 text-amber-400"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-amber-950/30 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-zinc-950 border border-emerald-500/30 rounded-3xl p-8 text-center" data-testid="booking-success">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-500/20 flex items-center justify-center">
            <Check className="w-8 h-8 text-emerald-400" />
          </div>
          <h2 className="text-2xl font-bold text-amber-100 mb-2">Réservation reçue !</h2>
          <p className="text-zinc-300 text-sm mb-6">{success.message}</p>
          {success.credit && <div className="mb-4 rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-3 text-xs text-emerald-200" data-testid="credit-applied">🎟️ Crédit de forfait appliqué — <b>{success.credit.label}</b> · {success.credit.credits_available} séance(s) restante(s). Aucun paiement à faire.</div>}
          <div className="bg-zinc-900 rounded-xl p-4 text-left text-sm space-y-2 text-zinc-200">
            <div className="flex items-center gap-2"><User className="w-4 h-4 text-amber-400" /> <span>{success.instructor.full_name}</span></div>
            <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-amber-400" /> <span>{formatDate(success.slot.start_at)}</span></div>
            <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-amber-400" /> <span>{formatTime(success.slot.start_at)} ({success.slot.duration_minutes} min)</span></div>
          </div>

          {/* Ajouter à l'agenda */}
          <div className="mt-5 space-y-2">
            <p className="text-xs text-zinc-400">Ajoute le cours à ton agenda :</p>
            <div className="grid grid-cols-2 gap-2">
              <a
                href={`${BACKEND}/api/booking/ics/${success.booking_id}`}
                download
                className="px-3 py-2 rounded-lg border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 text-xs font-semibold flex items-center justify-center gap-1"
                data-testid="dl-ics-btn"
              >
                📅 Apple / Outlook (.ics)
              </a>
              <button
                onClick={async () => {
                  try {
                    const r = await api.get(`/booking/google-url/${success.booking_id}`);
                    window.open(r.data.google_calendar_url, "_blank");
                  } catch (e) { toast.error("Erreur"); }
                }}
                className="px-3 py-2 rounded-lg border border-blue-500/40 bg-blue-500/10 hover:bg-blue-500/20 text-blue-300 text-xs font-semibold"
                data-testid="add-google-btn"
              >
                🗓️ Google Calendar
              </button>
            </div>
          </div>
          <button
            onClick={() => { setSuccess(null); setSelectedInstructor(null); setSelectedSlot(null); setForm({ client_name: "", client_email: "", client_phone: "", discipline: "", notes: "" }); }}
            className="mt-6 w-full bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold py-2 rounded-lg"
            data-testid="book-another-btn"
          >
            Réserver un autre cours
          </button>
        </div>
        <Toaster />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-amber-950/30 py-12 px-4" data-testid="public-booking-page">
      <Toaster />
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-end mb-2">
          <button onClick={() => setLang(lang === "fr" ? "en" : "fr")} data-testid="lang-toggle"
            className="text-xs font-semibold px-3 py-1.5 rounded-full border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 transition-colors">
            {t.langBtn}
          </button>
        </div>
        <div className="text-center mb-10">
          <div className="text-xs uppercase tracking-[0.3em] text-amber-400 mb-2">{t.heroEyebrow}</div>
          <h1 className="text-4xl sm:text-5xl font-bold text-amber-100 mb-3">{t.heroTitle}</h1>
          <p className="text-zinc-400 max-w-2xl mx-auto">
            {t.heroSub}
          </p>
        </div>

        {/* Step 1: Pick instructor */}
        {!selectedInstructor && (
          <div data-testid="step-instructor">
            <h2 className="text-amber-300 text-sm uppercase tracking-widest mb-3 text-center">{t.step1}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {instructors.map(i => (
                <button
                  key={i.id}
                  onClick={() => pickInstructor(i)}
                  className="text-left bg-zinc-950 border border-zinc-800 hover:border-amber-500/50 rounded-2xl p-5 transition group"
                  data-testid={`instructor-${i.id}`}
                >
                  <div className="text-lg font-bold text-amber-100 group-hover:text-amber-300">{i.full_name}</div>
                  <div className="text-xs text-amber-400 mt-1">{i.role}</div>
                  <div className="text-sm text-zinc-400 mt-3 line-clamp-3">{i.bio}</div>
                  <div className="flex items-center justify-between mt-4">
                    <span className="text-xs text-zinc-500">{CAD(i.default_rate_per_hour)}/heure</span>
                    <ArrowRight className="w-4 h-4 text-amber-400 group-hover:translate-x-1 transition" />
                  </div>
                </button>
              ))}
            </div>

            {comingSoon.length > 0 && (
              <div className="mt-10" data-testid="coming-soon-section">
                <h3 className="text-zinc-400 text-sm uppercase tracking-widest mb-3 text-center">Bientôt disponible</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl mx-auto">
                  {comingSoon.map((i) => (
                    <div key={i.id} className="bg-zinc-950/60 border border-zinc-800 rounded-2xl p-5" data-testid={`coming-soon-${i.id}`}>
                      <div className="flex items-center justify-between">
                        <div className="text-lg font-bold text-zinc-200">{i.full_name}</div>
                        <span className="text-[10px] uppercase tracking-wider bg-amber-500/15 text-amber-300 px-2 py-1 rounded-full">{i.available_from_label || "Bientôt"}</span>
                      </div>
                      <div className="text-xs text-amber-400/70 mt-1">{i.role}</div>
                      <div className="text-sm text-zinc-500 mt-3 line-clamp-2">{i.bio}</div>
                      {interestSent[i.id] ? (
                        <div className="mt-4 text-sm text-emerald-400 flex items-center gap-1.5">✓ On te préviendra !</div>
                      ) : interestFor?.id === i.id ? (
                        <div className="mt-4 space-y-2" data-testid={`interest-form-${i.id}`}>
                          <input value={interestForm.name} onChange={(e) => setInterestForm({ ...interestForm, name: e.target.value })} placeholder="Ton nom" className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100" data-testid={`interest-name-${i.id}`} />
                          <input value={interestForm.email} onChange={(e) => setInterestForm({ ...interestForm, email: e.target.value })} placeholder="Ton courriel" className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100" data-testid={`interest-email-${i.id}`} />
                          <input value={interestForm.phone} onChange={(e) => setInterestForm({ ...interestForm, phone: e.target.value })} placeholder="Téléphone (optionnel)" className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100" data-testid={`interest-phone-${i.id}`} />
                          <div className="flex gap-2">
                            <button onClick={() => submitInterest(i)} className="flex-1 bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold rounded-lg px-3 py-2 text-sm" data-testid={`interest-submit-${i.id}`}>Préviens-moi</button>
                            <button onClick={() => setInterestFor(null)} className="px-3 py-2 text-sm text-zinc-400">Annuler</button>
                          </div>
                        </div>
                      ) : (
                        <button onClick={() => { setInterestFor(i); setInterestForm({ name: "", email: "", phone: "" }); }} className="mt-4 w-full border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 rounded-lg px-3 py-2 text-sm transition" data-testid={`interest-btn-${i.id}`}>
                          🔔 Préviens-moi quand ses dispos ouvrent
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Pick slot */}
        {selectedInstructor && availability && !selectedSlot && (
          <div data-testid="step-slot">
            <div className="flex items-center gap-3 mb-4">
              <button onClick={() => { setSelectedInstructor(null); setAvailability(null); }} className="text-xs text-zinc-400 hover:text-amber-300">← Changer d'instructeur</button>
              <span className="text-zinc-700">·</span>
              <span className="text-amber-100 font-semibold">{selectedInstructor.full_name}</span>
            </div>
            <h2 className="text-amber-300 text-sm uppercase tracking-widest mb-3">{t.step2}</h2>
            <div className="flex items-center gap-2 mb-4" data-testid="duration-toggle">
              <span className="text-xs text-zinc-400 mr-1">Durée :</span>
              {[60, 90].map((d) => (
                <button
                  key={d}
                  onClick={() => changeDuration(d)}
                  className={`px-4 py-1.5 rounded-full text-sm font-semibold transition border ${
                    duration === d
                      ? "bg-amber-500 text-zinc-950 border-amber-500"
                      : "bg-zinc-900 text-amber-300 border-amber-500/30 hover:border-amber-500/60"
                  }`}
                  data-testid={`duration-${d}`}
                >
                  {d === 90 ? "1 h 30" : "1 heure"}
                </button>
              ))}
              <span className="text-xs text-zinc-500 ml-2">30 min : sur demande</span>
            </div>
            <div className="space-y-3">
              {Object.entries(availability.slots_by_date).map(([date, slots]) => {
                const available = slots.filter(s => s.available);
                if (available.length === 0) return null;
                return (
                  <div key={date} className="bg-zinc-950 border border-zinc-800 rounded-2xl p-4" data-testid={`date-${date}`}>
                    <div className="text-amber-200 font-semibold mb-2 capitalize">{formatDate(date + "T12:00:00")}</div>
                    <div className="flex flex-wrap gap-2">
                      {available.map((s) => (
                        <button
                          key={s.start_at}
                          onClick={() => setSelectedSlot(s)}
                          className="px-3 py-1.5 rounded-lg border border-amber-500/30 bg-amber-500/10 hover:bg-amber-500 hover:text-zinc-950 text-amber-300 text-sm transition"
                          data-testid={`slot-${s.start_at}`}
                        >
                          {formatTime(s.start_at)} <span className="text-[10px] opacity-70">({s.duration_minutes}min)</span>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {selectedInstructor && !availability && (
          <div className="flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-amber-400" /></div>
        )}

        {/* Step 3: Contact form */}
        {selectedSlot && (
          <div data-testid="step-form">
            <div className="flex items-center gap-3 mb-4">
              <button onClick={() => setSelectedSlot(null)} className="text-xs text-zinc-400 hover:text-amber-300">← Changer créneau</button>
              <span className="text-zinc-700">·</span>
              <span className="text-amber-100 font-semibold">{selectedInstructor.full_name}</span>
              <span className="text-zinc-700">·</span>
              <span className="text-amber-300">{formatDate(selectedSlot.start_at)} {formatTime(selectedSlot.start_at)}</span>
            </div>
            <h2 className="text-amber-300 text-sm uppercase tracking-widest mb-3">{t.step3}</h2>
            <div className="bg-zinc-950 border border-zinc-800 rounded-2xl p-6 space-y-3 max-w-xl">
              <input
                placeholder="Nom complet *"
                value={form.client_name}
                onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-amber-100"
                data-testid="form-name"
              />
              <input
                type="email"
                placeholder="Courriel *"
                value={form.client_email}
                onChange={(e) => setForm({ ...form, client_email: e.target.value })}
                onBlur={checkCredit}
                className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-amber-100"
                data-testid="form-email"
              />
              <input
                placeholder="Téléphone"
                value={form.client_phone}
                onChange={(e) => setForm({ ...form, client_phone: e.target.value })}
                className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-amber-100"
                data-testid="form-phone"
              />
              <select
                value={form.discipline}
                onChange={(e) => setForm({ ...form, discipline: e.target.value })}
                className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-amber-100"
                data-testid="form-discipline"
              >
                <option value="">Discipline souhaitée…</option>
                <option value="muay_thai">Kickboxing / Muay Thaï</option>
                <option value="jiu_jitsu_can_ryu">Jiu-jitsu Can Ryu</option>
                <option value="jiu_jitsu_bresilien">Jiu-Jitsu Brésilien</option>
                <option value="power_kicker">Power Kicker</option>
                <option value="autre">Autre / À discuter</option>
              </select>
              <textarea
                placeholder="Objectifs / questions (optionnel)"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-amber-100 min-h-[80px]"
                data-testid="form-notes"
              />
              {credit ? (
                <div className="bg-emerald-500/10 border border-emerald-500/40 rounded-lg p-3 text-xs text-emerald-200" data-testid="credit-detected">
                  <strong>🎟️ Forfait détecté :</strong> {credit.label} — {credit.beneficiaire} · {credit.credits_available} séance(s) restante(s).
                  <br />Ton crédit sera appliqué automatiquement à cette réservation : <b>aucun paiement à faire</b>.
                </div>
              ) : (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-xs text-amber-200">
                <strong>Tarif privé :</strong> {CAD(selectedSlot.duration_minutes === 90 ? 160 : 110)} pour {selectedSlot.duration_minutes === 90 ? "1 h 30" : "1 heure"} (kick-boxing / conditionnement).
                <br />Semi-privé jiu-jitsu : 160 $ (1 h) · 220 $ (1 h 30). Paiement sur place ou via lien Stripe après confirmation.
                <br />Membre d'un cours de groupe ? <a href="#forfaits" className="underline">Forfaits à tarif préférentiel</a> — le crédit s'applique automatiquement à la réservation.
              </div>
              )}
              <button
                onClick={reserve}
                disabled={submitting}
                className="w-full bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-zinc-950 font-bold py-3 rounded-lg flex items-center justify-center gap-2"
                data-testid="reserve-btn"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                Confirmer ma réservation
              </button>
            </div>
          </div>
        )}
        <div className="mt-10 max-w-3xl mx-auto"><MemberPackages /></div>
        <GiftCardSection lang={lang} />
      </div>
    </div>
  );
}
